import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Billing,
  Order,
  OrderStatus,
  Payment,
  PaymentOptions,
} from '../../shared/core/state/order/order.model';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import * as OrderActions from '../../shared/core/state/order/order.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderService } from '../../shared/core/state/order/order.service';
import { selectUser } from '../../shared/core/state/users/user.selectors';
import {
  PublicTenantPaymentConfig,
  TenantBankAccount,
} from '../../shared/models/invoice.model';
import { environment } from '../../../../src/environments/environment';
import { finalize, take } from 'rxjs';

@Component({
  selector: 'black-payment',
  templateUrl: './order-payment.component.html',
  styleUrls: ['./order-payment.component.scss'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class OrderPaymentComponent implements OnInit {
  private store = inject<Store<State>>(Store);
  private fb = inject(NonNullableFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private destroyRef = inject(DestroyRef);

  orderDetails: Order | null = null;
  selectedPaymentMethod: PaymentOptions | null = null;
  isLoading = true;
  isPayHereEnabled = false;
  isBankTransferEnabled = false;
  payhereError: string | null = null;
  tenantBankAccounts: TenantBankAccount[] = [];
  readonly PaymentOptions = PaymentOptions;

  get payableAmount(): number {
    if (!this.orderDetails) return 0;
    if (
      this.orderDetails.paymentPlan === 'INSTALLMENT' &&
      this.orderDetails.installments
    ) {
      const pending = this.orderDetails.installments.find(
        (i) => i.status === 'PENDING' || i.status === 'OVERDUE',
      );
      if (pending) return pending.amount;
    }
    return this.orderDetails.totalAmount ?? 0;
  }

  get currentInstallmentNumber(): number | null {
    if (!this.orderDetails || this.orderDetails.paymentPlan !== 'INSTALLMENT')
      return null;
    const pending = this.orderDetails.installments?.find(
      (i) => i.status === 'PENDING' || i.status === 'OVERDUE',
    );
    return pending
      ? pending.installmentNumber
      : this.orderDetails.currentInstallment || 1;
  }

  billingForm = this.fb.group({
    name: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    city: ['', Validators.required],
    province: ['', Validators.required],
    postal: ['', Validators.required],
    contact: ['', Validators.required],
    country: ['', Validators.required],
  });

  constructor() {}

  ngOnInit(): void {
    this.store.dispatch(OrderActions.resetPaymentStatus());

    const queryOrderId = this.route.snapshot.queryParamMap.get('orderId');

    const handleOrder = (order: Order | null) => {
      if (!order || order.status === OrderStatus.AWAITING_APPROVAL) {
        this.isLoading = false;
        this.router.navigate(['/dashboard/orders']);
        return;
      }
      if (
        order.status === OrderStatus.COMPLETED ||
        order.status === OrderStatus.CANCELLED ||
        order.status === OrderStatus.REFUNDED
      ) {
        this.isLoading = false;
        this.router.navigate(['/courses']);
        return;
      }
      this.orderDetails = order;

      if (order._id) {
        this.orderService
          .getOrderPaymentConfig(order._id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.isLoading = false;
            }),
          )
          .subscribe({
            next: (config) => this.applyPaymentConfig(config),
            error: (err) => {
              console.warn('Could not load order payment config:', err);
              this.applyPaymentConfig(null);
            },
          });
      } else {
        this.isLoading = false;
      }
    };

    if (queryOrderId) {
      this.orderService
        .getById(queryOrderId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (order) => {
            if (order)
              this.store.dispatch(
                OrderActions.placeOrderSuccess({ response: order }),
              );
            handleOrder(order);
          },
          error: () => handleOrder(null),
        });
    } else {
      this.store
        .select((store) => store.orders.selectedOrder)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedOrder) => handleOrder(selectedOrder));
    }

    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (!user) return;

        const fullName =
          [user.name?.firstName, user.name?.lastName]
            .filter(Boolean)
            .join(' ') || '';

        this.billingForm.patchValue({
          name: fullName || this.billingForm.controls.name.value,
          addressLine1:
            user.profile?.address?.addressLine1 ||
            this.billingForm.controls.addressLine1.value,
          addressLine2:
            user.profile?.address?.addressLine2 ||
            this.billingForm.controls.addressLine2.value,
          city:
            user.profile?.address?.city || this.billingForm.controls.city.value,
          province:
            user.profile?.address?.province ||
            this.billingForm.controls.province.value,
          postal:
            user.profile?.address?.postalCode ||
            this.billingForm.controls.postal.value,
          contact: user.mobile || this.billingForm.controls.contact.value,
          country:
            user.profile?.address?.country ||
            this.billingForm.controls.country.value,
        });
      });
  }

  private applyPaymentConfig(config: PublicTenantPaymentConfig | null) {
    if (!config) return;
    const rawBanks = config.bankAccounts || [];
    this.tenantBankAccounts = rawBanks.filter(
      (b) => b.bankName?.trim() && b.accountNumber?.trim(),
    );
    const isCustom = config.isSystemManaged === false;
    this.isPayHereEnabled = Boolean(
      config.acceptedMethods?.includes('PAYHERE') &&
        (!isCustom || Boolean(config.payhereMerchantId)),
    );
    this.isBankTransferEnabled = Boolean(
      (config.acceptedMethods
        ? config.acceptedMethods.includes('BANK_TRANSFER')
        : true) && this.tenantBankAccounts.length > 0,
    );

    const payhere = this.isPayHereEnabled ? PaymentOptions.PAYHERE : null;
    const bank = this.isBankTransferEnabled ? PaymentOptions.BANK_TRANSFER : null;
    const cur = this.selectedPaymentMethod;
    if ((cur === PaymentOptions.PAYHERE && !payhere) ||
        (cur === PaymentOptions.BANK_TRANSFER && !bank) ||
        !cur) {
      this.selectedPaymentMethod = payhere ?? bank;
    }
  }

  selectPaymentMethod(method: PaymentOptions) {
    this.selectedPaymentMethod = method;
  }

  onBankPaymentSuccess(payment: Payment) {
    const paymentWithAmount: Payment = {
      ...payment,
      amount: payment.amount ?? this.payableAmount,
      currencyCode:
        payment.currencyCode ?? this.orderDetails?.currency ?? 'LKR',
    };

    this.store.dispatch(
      OrderActions.processPayment({
        orderId: this.orderDetails?._id ?? '',
        payment: paymentWithAmount,
      }),
    );

    this.store
      .select((store) => store.orders.isPaymentCompleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isPaymentCompleted) => {
        if (isPaymentCompleted) {
          this.router.navigate(['/order/payment-success']);
        }
      });
  }

  processPayment() {
    this.billingForm.markAllAsTouched();
    if (this.billingForm.valid) {
      this.processPayherePayment();
    }
  }

  private processPayherePayment() {
    const billingData = this.billingForm.value;

    const billing: Billing = {
      name: billingData.name ?? '',
      addressLine1: billingData.addressLine1 ?? '',
      addressLine2: billingData.addressLine2 ?? '',
      city: billingData.city ?? '',
      province: billingData.province ?? '',
      postalCode: billingData.postal ?? '',
      contactNumber: billingData.contact ?? '',
      country: billingData.country ?? '',
    };

    const returnUrl = `${window.location.origin}/order/payment-success`;
    const cancelUrl = window.location.href;

    this.orderService
      .initiatePayherePayment(
        this.orderDetails?._id ?? '',
        billing,
        returnUrl,
        cancelUrl,
      )
      .subscribe({
        next: (checkoutData) => {
          this.payhereError = null;
          this.submitPayhereForm(checkoutData);
        },
        error: (err) => {
          console.error('PayHere initiation failed:', err);
          this.payhereError =
            'Payment processing is currently unavailable. Please try again later or contact the academy administrator.';
        },
      });
  }

  private submitPayhereForm(checkoutData: Record<string, string>) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `https://${
      environment.production ? 'www' : 'sandbox'
    }.payhere.lk/pay/checkout`;

    Object.keys(checkoutData).forEach((key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = checkoutData[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }
}
